import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarCheck, Clock, MagnifyingGlass, CircleNotch, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PublicBooking() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [day, setDay] = useState(todayISO());
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [remind, setRemind] = useState(true);
  const [credits, setCredits] = useState(null);
  const [creditId, setCreditId] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [slotRefresh, setSlotRefresh] = useState(0);
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState("");

  useEffect(() => {
    api.get(`/public/booking/${slug}`).then((r) => setPage(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const service = page?.services?.find((s) => s.id === serviceId);

  useEffect(() => {
    setSlots(null); setSlot("");
    if (!serviceId || !day) return;
    api.get(`/public/booking/${slug}/availability`, { params: { service_id: serviceId, day } })
      .then((r) => setSlots(r.data)).catch(() => setSlots({ slots: [] }));
  }, [slug, serviceId, day, slotRefresh]);

  const lookup = async () => {
    if (!name.trim()) return toast.error("Enter your name first.");
    try {
      const { data } = await api.post(`/public/booking/${slug}/lookup`, { client_name: name.trim() });
      setCredits(data.credits);
      setCreditId("");
      if (!data.credits.length) toast.info("No active packages found under that name — you can still book and pay per session.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't look that up."); }
  };

  const eligibleCredits = (credits || []).filter((c) => !service?.equipment || !c.equipment || c.equipment.toLowerCase() === service.equipment.toLowerCase());

  const book = async () => {
    if (!service) return toast.error("Pick a service.");
    if (!slot) return toast.error("Pick a time slot.");
    if (!name.trim()) return toast.error("Add your name.");
    if (!ack) return toast.error("Please confirm the safety acknowledgment.");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/booking/${slug}/book`, {
        service_id: serviceId, date: day, start_time: slot, client_name: name.trim(),
        credit_id: creditId || null, safety_ack: true,
        client_email: email.trim(), reminder_opt_in: remind && !!email.trim(),
      });
      setDone(data);
      setSlotRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't book that slot.");
      if (e?.response?.status === 409) setSlotRefresh((n) => n + 1);
    } finally { setBusy(false); }
  };

  const joinWaitlist = async () => {
    if (!wlName.trim() || !wlEmail.trim()) return toast.error("Add your name and email — we'll email you when a spot opens.");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/booking/${slug}/waitlist`, {
        service_id: serviceId, date: day, client_name: wlName.trim(), client_email: wlEmail.trim(),
      });
      setWlDone(data.rowan_message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't join the waitlist."); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bk-bg, #F8F7F4)" }}>
      <p className="text-base" style={label} data-testid="public-booking-notfound">This booking page isn't available.</p>
    </div>
  );
  if (!page) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>
      <CircleNotch size={28} className="animate-spin" color="var(--bk-accent)" />
    </div>
  );

  if (done) return (
    <div className="min-h-screen px-4 py-16" style={{ background: "#F8F7F4" }}>
      <div className="max-w-xl mx-auto bk-card p-8 text-center" data-testid="public-booking-confirmation">
        <CheckCircle size={44} weight="fill" color="var(--bk-success)" className="mx-auto" />
        <h1 className="font-display text-2xl font-bold mt-4" style={{ color: "var(--bk-text)" }}>You're booked!</h1>
        <p className="text-base mt-3" style={{ color: "var(--bk-text)" }}>{done.confirmation}</p>
        <div className="rounded-xl border px-4 py-3 mt-5 text-sm text-left" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="public-booking-cancel-rules">
          <p className="font-semibold mb-1" style={{ color: "var(--bk-text)" }}>Cancellation policy</p>
          {done.cancel_text}
          {done.cancel_window_hours ? ` Self-service changes close ${done.cancel_window_hours} hours before your slot.` : ""}
        </div>
        {done.manage_path && (
          <a href={done.manage_path} data-testid="public-manage-link"
            className="inline-block mt-4 text-sm font-semibold underline" style={{ color: "var(--bk-accent)" }}>
            Need to change it? Manage your booking →
          </a>
        )}
        <button onClick={() => { setDone(null); setSlot(""); setAck(false); setSlotRefresh((n) => n + 1); }} data-testid="public-booking-again"
          className="bk-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold mt-6">Book another session</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: "#F8F7F4" }} data-testid="public-booking-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="public-booking-name">{page.business_name}</h1>
          <p className="text-sm mt-1" style={label}>Book a session online · open {page.hours_start}–{page.hours_end}</p>
        </div>

        <div className="bk-card p-5">
          <p className="font-semibold text-sm mb-3" style={{ color: "var(--bk-text)" }}>1. Pick a service</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="public-services-container">
            {page.services.map((s) => (
              <button key={s.id} onClick={() => setServiceId(s.id)} data-testid={`public-service-${s.id}`}
                className="text-left rounded-xl border p-3 transition-colors"
                style={{ borderColor: serviceId === s.id ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: serviceId === s.id ? 2 : 1, background: serviceId === s.id ? "var(--bk-rowan-bg)" : "#fff" }}>
                <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>{s.name}</p>
                <p className="text-xs mt-0.5" style={label}>
                  ${Number(s.price).toLocaleString()}{s.duration_min ? ` · ${s.duration_min} min` : ""}{s.sessions_included ? ` · ${s.sessions_included} sessions` : ""}{s.equipment ? ` · ${s.equipment}` : ""}
                </p>
                <p className="text-[11px] mt-1" style={label}>{s.cancel_text}</p>
              </button>
            ))}
          </div>
        </div>

        {service && (
          <div className="bk-card p-5">
            <p className="font-semibold text-sm mb-3" style={{ color: "var(--bk-text)" }}>2. Pick a date &amp; time</p>
            <DateInput min={todayISO()} value={day} onChange={(e) => setDay(e.target.value)} data-testid="public-booking-date"
              className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            {slots === null ? (
              <p className="text-sm mt-3" style={label}>Checking availability…</p>
            ) : slots.slots.length === 0 ? (
              <div className="mt-3" data-testid="public-no-slots">
                <p className="text-sm" style={label}>{slots.note || "No open slots that day — try another date."}</p>
                {wlDone ? (
                  <p className="text-sm mt-3 rounded-xl border px-4 py-3" style={{ borderColor: "var(--bk-border)", background: "var(--bk-rowan-bg)", color: "var(--bk-text)" }} data-testid="waitlist-confirmation">{wlDone}</p>
                ) : (
                  <div className="mt-3 rounded-xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid="waitlist-form">
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Join the waitlist for this day</p>
                    <p className="text-xs mt-0.5" style={label}>If a spot opens, we'll email the first person in line — the offer is theirs for a few hours before it passes on.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <input value={wlName} onChange={(e) => setWlName(e.target.value)} placeholder="Your name" data-testid="waitlist-name"
                        className="flex-1 min-w-[160px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                      <input value={wlEmail} onChange={(e) => setWlEmail(e.target.value)} placeholder="Email" type="email" data-testid="waitlist-email"
                        className="flex-1 min-w-[180px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                      <button onClick={joinWaitlist} disabled={busy} data-testid="waitlist-join-btn"
                        className="bk-btn-primary px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">Join waitlist</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="public-slots-container">
                {slots.slots.map((t) => (
                  <button key={t} onClick={() => setSlot(t)} data-testid={`public-slot-${t}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={slot === t ? { background: "var(--bk-accent)", color: "#fff", borderColor: "var(--bk-accent)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <Clock size={13} weight="bold" /> {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {service && slot && (
          <div className="bk-card p-5 space-y-3">
            <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>3. Who's coming?</p>
            <div className="flex flex-wrap items-center gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="public-client-name"
                className="flex-1 min-w-[200px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <button onClick={lookup} data-testid="public-lookup-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                <MagnifyingGlass size={13} weight="bold" /> Find my package
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email (optional — for reminders)" data-testid="public-client-email"
                className="flex-1 min-w-[220px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              {email.trim() && (
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--bk-text)" }}>
                  <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} data-testid="public-reminder-optin" />
                  Email me a reminder the day before
                </label>
              )}
            </div>
            {credits !== null && eligibleCredits.length > 0 && (
              <div className="space-y-1.5" data-testid="public-credit-list">
                <p className="text-xs font-semibold" style={label}>Use a package or membership (optional):</p>
                {eligibleCredits.map((c) => (
                  <label key={c.credit_id} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }} data-testid={`public-credit-${c.credit_id}`}>
                    <input type="radio" name="credit" checked={creditId === c.credit_id} onChange={() => setCreditId(creditId === c.credit_id ? "" : c.credit_id)} />
                    {c.item_name} — <span className="font-semibold" style={{ color: "var(--bk-success)" }}>{c.sessions_remaining} session{c.sessions_remaining !== 1 ? "s" : ""} left</span>
                    {c.expires_at ? <span className="text-xs" style={label}>· expires {c.expires_at}</span> : null}
                  </label>
                ))}
              </div>
            )}
            <div className="rounded-xl border px-4 py-3" style={{ borderColor: "rgba(232,168,56,0.5)", background: "#FFF9F0" }}>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="public-safety-ack" className="mt-0.5" />
                <span><ShieldCheck size={15} weight="fill" color="#E8A838" className="inline mr-1" />{page.safety_text}</span>
              </label>
            </div>
            <button onClick={book} disabled={busy || !ack} data-testid="public-book-btn"
              className="bk-btn-primary w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold disabled:opacity-50">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <CalendarCheck size={16} weight="bold" />}
              Book {service.name} · {day} at {slot}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
